import React from 'react';
import { motion } from 'framer-motion';
import { CoopQuizSession } from '../hooks/useCoopQuiz';

interface CoopQuizResultsProps {
  session: CoopQuizSession;
  myPlayerNumber: 1 | 2;
  onPlayAgain: () => void;
  onExit: () => void;
  getPlayer1DisplayData: () => { displayName: string; profileImage: string };
  getPlayer2DisplayData: () => { displayName: string; profileImage: string };
}

export const CoopQuizResults: React.FC<CoopQuizResultsProps> = ({
  session,
  myPlayerNumber,
  onPlayAgain,
  onExit,
  getPlayer1DisplayData,
  getPlayer2DisplayData
}) => {
  const player1Score = session.scores.player1;
  const player2Score = session.scores.player2;

  // Get display data separately from session data
  const player1Display = getPlayer1DisplayData();
  const player2Display = getPlayer2DisplayData();

  const winner =
    player1Score > player2Score ? 1 :
    player2Score > player1Score ? 2 :
    0; // tie

  const iWon = winner === myPlayerNumber;
  const isTie = winner === 0;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="space-y-6"
    >
      {/* Result Icon */}
      <div className="text-center">
        <div className="text-8xl mb-4">
          {isTie ? '🤝' : iWon ? '🎉' : '😢'}
        </div>
        <h2 className="text-3xl font-bold text-purple-400 mb-2">
          {isTie ? '¡Empate!' : iWon ? '¡Ganaste!' : '¡Perdiste!'}
        </h2>
        <p className="text-gray-400">
          {isTie
            ? 'Ambos jugadores tienen el mismo puntaje'
            : iWon
            ? '¡Felicidades! Conoces mejor los gustos musicales'
            : 'El otro jugador conoce mejor los gustos musicales'}
        </p>
      </div>

      {/* Scores */}
      <div className="grid grid-cols-2 gap-6">
        {/* Player 1 Score */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className={`p-6 rounded-lg border-2 ${
            winner === 1
              ? 'bg-green-500/20 border-green-500'
              : 'bg-white/5 border-white/20'
          }`}
        >
          <div className="text-center space-y-2">
            <img
              src={player1Display.profileImage}
              alt={player1Display.displayName}
              className="w-20 h-20 rounded-full border-4 border-green-400 mx-auto"
            />
            <h3 className="text-lg font-bold text-green-400">
              {player1Display.displayName}
            </h3>
            <div className="text-5xl font-bold text-white">{player1Score}</div>
            <p className="text-sm text-gray-400">
              de {session.questions.length} preguntas
            </p>
            {winner === 1 && (
              <div className="text-2xl">👑</div>
            )}
          </div>
        </motion.div>

        {/* Player 2 Score */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className={`p-6 rounded-lg border-2 ${
            winner === 2
              ? 'bg-pink-500/20 border-pink-500'
              : 'bg-white/5 border-white/20'
          }`}
        >
          <div className="text-center space-y-2">
            <img
              src={player2Display.profileImage}
              alt={player2Display.displayName}
              className="w-20 h-20 rounded-full border-4 border-pink-400 mx-auto"
            />
            <h3 className="text-lg font-bold text-pink-400">
              {player2Display.displayName}
            </h3>
            <div className="text-5xl font-bold text-white">{player2Score}</div>
            <p className="text-sm text-gray-400">
              de {session.questions.length} preguntas
            </p>
            {winner === 2 && (
              <div className="text-2xl">👑</div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 justify-center">
        <button
          onClick={onPlayAgain}
          className="px-8 py-3 bg-purple-500 text-white rounded-full font-bold hover:bg-purple-600 transition-all hover:scale-105"
        >
          🔄 Jugar Otra Vez
        </button>
        <button
          onClick={onExit}
          className="px-8 py-3 bg-white/10 text-white rounded-full font-bold hover:bg-white/20 transition-all"
        >
          Salir
        </button>
      </div>
    </motion.div>
  );
};
